import { useEffect, useState } from "react";

export type SessionWhoami = {
  active: {
    name: string;
    username: string;
    avatarUrl: string;
  } | null;
  remembered: {
    label: string;
    maskedEmail: string;
    avatarUrl: string;
    hasDiscord: boolean;
  } | null;
};

export type SessionWhoamiResult = SessionWhoami & {
  status: "pending" | "resolved" | "unavailable";
};

export const PENDING_SESSION_WHOAMI: SessionWhoamiResult = {
  status: "pending",
  active: null,
  remembered: null,
};

export const UNAVAILABLE_SESSION_WHOAMI: SessionWhoamiResult = {
  status: "unavailable",
  active: null,
  remembered: null,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseActiveSession(
  value: unknown
): SessionWhoami["active"] | undefined {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    typeof value.username !== "string" ||
    typeof value.avatarUrl !== "string"
  ) {
    return undefined;
  }
  return {
    name: value.name,
    username: value.username,
    avatarUrl: value.avatarUrl,
  };
}

function parseRememberedSession(
  value: unknown
): SessionWhoami["remembered"] | undefined {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    typeof value.label !== "string" ||
    typeof value.maskedEmail !== "string" ||
    typeof value.avatarUrl !== "string" ||
    typeof value.hasDiscord !== "boolean"
  ) {
    return undefined;
  }
  return {
    label: value.label,
    maskedEmail: value.maskedEmail,
    avatarUrl: value.avatarUrl,
    hasDiscord: value.hasDiscord,
  };
}

function parseSessionWhoami(value: unknown): SessionWhoami | null {
  if (!isRecord(value) || !("active" in value) || !("remembered" in value)) {
    return null;
  }
  const active = parseActiveSession(value.active);
  const remembered = parseRememberedSession(value.remembered);
  if (active === undefined || remembered === undefined) return null;
  return { active, remembered };
}

export function sessionWhoamiAfterRefresh(
  current: SessionWhoamiResult,
  refreshed: SessionWhoamiResult
): SessionWhoamiResult {
  return refreshed.status === "unavailable" ? current : refreshed;
}

export async function loadSessionWhoami(
  fetcher: typeof fetch = fetch,
  signal?: AbortSignal
): Promise<SessionWhoamiResult> {
  try {
    const response = await fetcher("/api/session/whoami", {
      cache: "no-store",
      signal,
    });
    if (!response.ok) return { ...UNAVAILABLE_SESSION_WHOAMI };

    const session = parseSessionWhoami(await response.json());
    if (!session) return { ...UNAVAILABLE_SESSION_WHOAMI };
    return {
      status: "resolved",
      active: session.active,
      remembered: session.remembered,
    };
  } catch (error) {
    if (
      signal?.aborted &&
      (error as { name?: string } | null)?.name === "AbortError"
    ) {
      throw error;
    }
    return { ...UNAVAILABLE_SESSION_WHOAMI };
  }
}

export function useSessionWhoami() {
  const [session, setSession] = useState<SessionWhoamiResult>(
    PENDING_SESSION_WHOAMI
  );

  useEffect(() => {
    let mounted = true;
    let refreshNumber = 0;
    let controller: AbortController | null = null;

    const refresh = () => {
      controller?.abort();
      controller = new AbortController();
      const thisRefresh = ++refreshNumber;

      void loadSessionWhoami(fetch, controller.signal)
        .then((refreshed) => {
          if (!mounted || thisRefresh !== refreshNumber) return;
          setSession((current) =>
            sessionWhoamiAfterRefresh(current, refreshed)
          );
        })
        .catch((error) => {
          if (error?.name !== "AbortError") {
            setSession((current) =>
              sessionWhoamiAfterRefresh(current, UNAVAILABLE_SESSION_WHOAMI)
            );
          }
        });
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) refresh();
    };

    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      mounted = false;
      controller?.abort();
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return session;
}
